/**
 * The shop's chrome: announcement strip, the dark brand bar (logo, search, account, cart), the
 * nav row with the "Shop by department" mega menu, the phone drawer menu and the cart drawer.
 * Written from scratch for this shop (docs/storefront-warehouse-spec.md); every navigation here is
 * a real <a href> so crawlers and middle-clicks work.
 */
import { useEffect, useId, useRef, useState } from "react";
import type { Account, NavNode, SearchResponse, ShopConfig } from "./types";
import { lineRoute, type Basket } from "./basket";
import { Go, MOUNT, type GoFn, type Route } from "./route";
import { deptRoute } from "./nav";
import { freeDeliveryLine, get, rands } from "./shared";
import { Pic, sizedSrc } from "./img";
import {
  IconCart, IconFacebook, IconInstagram, IconTikTok, IconWhatsApp, IconChevronLeft, IconChevronRight, IconMenu, IconMinus, IconPlus, IconSearch, IconUser, IconCamera,
} from "./icons";
import { ReviewBadge, useReviews } from "./Reviews";
import { Drawer, Highlight, useDebounced } from "./ui";
import { resolveContact, SOCIAL_LINKS, WHATSAPP_CHANNEL_URL } from "../../../src/shop-contact";
import { MegaMenu, NavThumb } from "./DeptMenu";
import { PhotoSearch } from "./PhotoSearch";
import { useBundleThemes } from "./bundles-ui";

export const ASSURANCES = [
  "Delivery 2–7 working days",
  "Secure card & instant EFT checkout",
  "14-day returns on unused goods",
];

/**
 * The dropdown thumbnail is a plain eager <img>, not <Pic>: the list is `hidden` (display:none)
 * until it opens, and a loading="lazy" image inside it, with a width-suffixed srcset, was left
 * unrequested in Chrome, so every thumb stayed a grey box. 96px covers a 48px slot at 2x.
 */
function thumbSrc(url: string): string {
  return sizedSrc(url, 96);
}

/* ── Predictive search: an ARIA combobox ───────────────────────────────────────────────────── */
type Opt =
  | { kind: "cat"; id: string; name: string; slug: string; path: string; productCount: number }
  | { kind: "prod"; handle: string; title: string; image: string | null; priceCents: number | null; wasCents: number | null }
  | { kind: "all" };

function SearchBox({ roots, route, go, trade, onPhoto }: { roots: NavNode[]; route: Route; go: GoFn; trade: boolean; onPhoto: () => void }) {
  const uid = useId();
  const listId = `${uid}-list`;
  const initial = route.name === "search" ? new URLSearchParams(route.qs ?? "") : null;
  const [q, setQ] = useState(initial?.get("q") ?? "");
  const [scope, setScope] = useState(initial?.get("in") ?? "");
  const [open, setOpen] = useState(false);
  const [res, setRes] = useState<SearchResponse | null>(null);
  const [active, setActive] = useState(-1);
  const box = useRef<HTMLFormElement | null>(null);
  const input = useRef<HTMLInputElement | null>(null);
  const dq = useDebounced(q.trim(), 200);

  useEffect(() => {
    if (dq.length < 2) { setRes(null); return; }
    const ac = new AbortController();
    get<SearchResponse>(`/shop/search?q=${encodeURIComponent(dq)}${scope ? `&in=${encodeURIComponent(scope)}` : ""}&limit=6`, ac.signal)
      .then((d) => { if (!ac.signal.aborted) { setRes(d); setActive(-1); } })
      .catch(() => { if (!ac.signal.aborted) setRes(null); });
    return () => ac.abort();
  }, [dq, scope]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const options: Opt[] = res && dq.length >= 2
    ? [
        ...res.categories.map((c): Opt => ({ kind: "cat", ...c })),
        ...res.products.map((p): Opt => ({ kind: "prod", handle: p.handle, title: p.title, image: p.image, priceCents: p.priceCents, wasCents: p.wasCents })),
        ...(res.products.length + res.categories.length > 0 ? [{ kind: "all" } as Opt] : []),
      ]
    : [];
  const showList = open && dq.length >= 2 && res !== null;

  const searchRoute = (): Route => {
    const p = new URLSearchParams();
    p.set("q", q.trim());
    if (scope) p.set("in", scope);
    return { name: "search", qs: p.toString() };
  };
  const choose = (o: Opt) => {
    setOpen(false);
    if (o.kind === "cat") go(deptRoute(o));
    else if (o.kind === "prod") go({ name: "product", handle: o.handle });
    else go(searchRoute());
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((a) => (options.length ? (a + 1) % options.length : -1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (options.length ? (a <= 0 ? options.length - 1 : a - 1) : -1)); }
    else if (e.key === "Escape") { if (open) { e.preventDefault(); setOpen(false); setActive(-1); } }
    else if (e.key === "Enter" && showList && active >= 0 && options[active]) { e.preventDefault(); choose(options[active]!); }
  };
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (q.trim().length === 0) return;
    setOpen(false);
    go(searchRoute());
    input.current?.blur();
  };

  let idx = -1;
  const optId = (i: number) => `${uid}-opt-${i}`;
  return (
    <form ref={box} className="sbox" role="search" onSubmit={submit} action={`${MOUNT}/search`}>
      <label className="sr-only" htmlFor={`${uid}-scope`}>Search in</label>
      <select id={`${uid}-scope`} name="in" className="sbox-scope" value={scope} onChange={(e) => setScope(e.target.value)}>
        <option value="">All categories</option>
        {roots.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
      </select>
      <label className="sr-only" htmlFor={`${uid}-q`}>Search products</label>
      <input
        ref={input}
        id={`${uid}-q`}
        name="q"
        type="search"
        className="sbox-input"
        placeholder={trade ? "Search products (wholesale pricing)" : "Search products"}
        autoComplete="off"
        value={q}
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showList && active >= 0 ? optId(active) : undefined}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
      />
      <button type="button" className="sbox-cam" aria-label="Search by photo" title="Search by photo" aria-haspopup="dialog" onClick={() => { setOpen(false); onPhoto(); }}><IconCamera size={22} /></button>
      <button type="submit" className="sbox-btn" aria-label="Search"><IconSearch size={20} /></button>
      <div id={listId} role="listbox" aria-label="Search suggestions" className="sbox-list" hidden={!showList}>
        {showList && options.length === 0 && <div className="sbox-empty" role="presentation">No matches for “{dq}”. Try a shorter word.</div>}
        {showList && res!.categories.length > 0 && (
          <div role="group" aria-label="Categories">
            <div className="sbox-h" role="presentation">Categories</div>
            {res!.categories.map((c) => {
              idx += 1; const i = idx;
              return (
                <a key={c.id} id={optId(i)} role="option" aria-selected={active === i} className={`sbox-opt${active === i ? " on" : ""}`}
                  href={`${MOUNT}/d/${encodeURIComponent(`${c.id}-${c.slug}`)}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => { e.preventDefault(); choose({ kind: "cat", ...c }); }}>
                  <span><Highlight text={c.name} q={dq} /><small>{c.path.replace(/ > /g, " › ")}</small></span>
                  <small>{c.productCount}</small>
                </a>
              );
            })}
          </div>
        )}
        {showList && res!.products.length > 0 && (
          <div role="group" aria-label="Products">
            <div className="sbox-h" role="presentation">Products</div>
            {res!.products.map((p) => {
              idx += 1; const i = idx;
              return (
                <a key={p.handle} id={optId(i)} role="option" aria-selected={active === i} className={`sbox-opt sbox-prod${active === i ? " on" : ""}`}
                  href={`${MOUNT}/p/${encodeURIComponent(p.handle)}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => { e.preventDefault(); choose({ kind: "prod", handle: p.handle, title: p.title, image: p.image, priceCents: p.priceCents, wasCents: p.wasCents }); }}>
                  <span className="sbox-thumb">{p.image && <img src={thumbSrc(p.image)} alt="" width={48} height={48} loading="eager" decoding="sync" referrerPolicy="no-referrer" />}</span>
                  <span className="sbox-title"><Highlight text={p.title} q={dq} /></span>
                  <b className="money">{rands(p.priceCents)}</b>
                </a>
              );
            })}
          </div>
        )}
        {showList && options.length > 0 && (() => {
          idx += 1; const i = idx;
          return (
            <a id={optId(i)} role="option" aria-selected={active === i} className={`sbox-opt sbox-all${active === i ? " on" : ""}`}
              href={`${MOUNT}/search?q=${encodeURIComponent(q.trim())}${scope ? `&in=${encodeURIComponent(scope)}` : ""}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => { e.preventDefault(); choose({ kind: "all" }); }}>
              View all results →
            </a>
          );
        })()}
      </div>
    </form>
  );
}

/* ── Phone menu: a left drawer that drills down the department tree ────────────────────────── */
function MobileMenu({ open, onClose, roots, go, account }: { open: boolean; onClose: () => void; roots: NavNode[]; go: GoFn; account: Account | null }) {
  const [stack, setStack] = useState<NavNode[]>([]);
  useEffect(() => { if (!open) setStack([]); }, [open]);
  const here = stack[stack.length - 1] ?? null;
  const bundleThemes = useBundleThemes();
  const list = here ? here.children : roots;
  return (
    <Drawer open={open} onClose={onClose} side="left" title="Menu">
      {here ? (
        <>
          <button type="button" className="mm-back" onClick={() => setStack((s) => s.slice(0, -1))}><IconChevronLeft size={18} /> Back</button>
          <Go to={deptRoute(here)} go={go} className="mm-row mm-strong" onNavigate={onClose}>Shop all {here.name}</Go>
        </>
      ) : (
        <p className="mm-label">Shop by department</p>
      )}
      <ul className="mm-list">
        {list.map((n) => (
          <li key={n.id}>
            {n.children.length > 0 ? (
              <button type="button" className="mm-row" onClick={() => setStack((s) => [...s, n])}>
                <span className="mm-name">{n.image !== undefined && <NavThumb node={n} size={36} className="mm-thumb" />}{n.name}</span><IconChevronRight size={18} />
              </button>
            ) : (
              <Go to={deptRoute(n)} go={go} className="mm-row" onNavigate={onClose}><span className="mm-name">{n.image !== undefined && <NavThumb node={n} size={36} className="mm-thumb" />}{n.name}</span></Go>
            )}
          </li>
        ))}
      </ul>
      {!here && (
        <ul className="mm-list mm-extra">
          <li><a className="mm-row" href={`${MOUNT}/#deal`} onClick={(e) => { e.preventDefault(); onClose(); go({ name: "home" }); window.setTimeout(() => (document.getElementById("deal") ?? document.getElementById("specials"))?.scrollIntoView(), 60); }}>Deal of the day</a></li>
          <li><Go to={{ name: "clearance" }} go={go} className="mm-row" onNavigate={onClose}>Clearance sale</Go></li>
          <li>
            <Go to={{ name: "bundles" }} go={go} className="mm-row" onNavigate={onClose}>Bundles &amp; Kits</Go>
            {bundleThemes.length > 0 && (
              <ul className="mm-sub">
                {bundleThemes.map((t) => <li key={t.slug}><Go to={{ name: "bundles", theme: t.slug }} go={go} className="mm-row mm-subrow" onNavigate={onClose}>{t.theme}</Go></li>)}
              </ul>
            )}
          </li>
          <li><Go to={{ name: "collections" }} go={go} className="mm-row" onNavigate={onClose}>All collections</Go></li>
          <li><Go to={{ name: "guides" }} go={go} className="mm-row" onNavigate={onClose}>Buying guides</Go></li>
          <li><Go to={{ name: "faq" }} go={go} className="mm-row" onNavigate={onClose}>Help</Go></li>
          <li><Go to={{ name: "contact" }} go={go} className="mm-row" onNavigate={onClose}>Contact</Go></li>
          <li><Go to={{ name: "account" }} go={go} className="mm-row" onNavigate={onClose}>{account ? "My account" : "Login / Sign up"}</Go></li>
          {account && <li><Go to={{ name: "orders" }} go={go} className="mm-row" onNavigate={onClose}>My orders</Go></li>}
          <li><Go to={{ name: "track" }} go={go} className="mm-row" onNavigate={onClose}>Track an order</Go></li>
          <li><Go to={{ name: "account", tab: "up" }} go={go} className="mm-row" onNavigate={onClose}>Wholesale account</Go></li>
        </ul>
      )}
    </Drawer>
  );
}

/* ── Top bar: visit-us address (from Settings) and the social / WhatsApp channel links ─────── */
const STRIP_EXT = { target: "_blank", rel: "noopener noreferrer" } as const;
const STRIP_ICON = { facebook: <IconFacebook size={16} />, instagram: <IconInstagram size={16} />, tiktok: <IconTikTok size={16} /> } as const;

function StripSocial() {
  return (
    <ul className="strip-social">
      {SOCIAL_LINKS.map((s) => (
        <li key={s.id}>
          <a href={s.url} {...STRIP_EXT} aria-label={`Z S MERCH on ${s.name} (opens in a new tab)`}>{STRIP_ICON[s.id]}</a>
        </li>
      ))}
      <li>
        <a href={WHATSAPP_CHANNEL_URL} {...STRIP_EXT} className="strip-wa">
          <IconWhatsApp size={16} /> <span className="strip-wa-t">Join our WhatsApp channel</span><span className="sr-only"> (opens in a new tab)</span>
        </a>
      </li>
    </ul>
  );
}

/* ── The header ────────────────────────────────────────────────────────────────────────────── */
export function Header({ roots, go, route, basket, account, config, onCart }: {
  roots: NavNode[]; go: GoFn; route: Route; basket: Basket; account: Account | null; config: ShopConfig | null; onCart: () => void;
}) {
  const [menu, setMenu] = useState(false);
  const [photo, setPhoto] = useState(false);
  const [down, setDown] = useState(false);
  const trade = account?.list === "wholesale";
  const reviews = useReviews();
  const pending = account?.tradeStatus === "pending";
  const last = useRef(0);
  const { collect } = resolveContact(config);
  /** Bundles & Kits themes for the menu — one shared, edge-cached read (bundles-ui.tsx). */
  const bundleThemes = useBundleThemes();

  /** Shrinks on scroll-down, returns on scroll-up. Passive listener; one boolean of state. */
  useEffect(() => {
    last.current = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const dy = y - last.current;
      if (Math.abs(dy) < 6) return;
      setDown(y > 120 && dy > 0);
      last.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <div className="strip">
        <div className="wrap strip-row">
          {pending ? (
            <span>Wholesale application pending — retail prices shown</span>
          ) : (
            <a href={collect.mapUrl} {...STRIP_EXT} className="strip-visit">
              <span className="strip-visit-l">Visit us</span> <span className="strip-addr">{collect.address}</span>
              <span className="sr-only"> (opens map in a new tab)</span>
            </a>
          )}
          {reviews && <ReviewBadge r={reviews} className="strip-rating" />}
          <StripSocial />
        </div>
      </div>
      <header className={`site-head${down ? " is-down" : ""}`}>
        <div className="bar">
          <div className="wrap bar-row">
            <button type="button" className="icon-btn bar-burger" onClick={() => setMenu(true)} aria-label="Open menu"><IconMenu size={24} /></button>
            <Go to={{ name: "home" }} go={go} className="logo">
              <picture>
                <source type="image/webp" srcSet={`/brand/zs-mark-40.webp 1x, /brand/zs-mark-40@2x.webp 2x`} />
                <img src={`/brand/zs-mark-40.png`} srcSet={`/brand/zs-mark-40@2x.png 2x`} width={26} height={40} alt="" className="logo-mark" />
              </picture>
              <span className="logo-word">Z S MERCH</span>
            </Go>
            <SearchBox roots={roots} route={route} go={go} trade={trade} onPhoto={() => setPhoto(true)} />
            <Go to={{ name: "account" }} go={go} className="bar-acct">
              <IconUser size={24} />
              <span className="bar-acct-t">
                <small>{account ? `Hi, ${account.name.split(" ")[0]}` : "Login / Sign up"}</small>
                <b>My account</b>
              </span>
              {trade && <span className="pill-trade">Wholesale pricing</span>}
            </Go>
            <Go to={account ? { name: "orders" } : { name: "track" }} go={go} className="bar-acct bar-orders">
              <span className="bar-acct-t">
                <small>{account ? "Your" : "Order status?"}</small>
                <b>{account ? "My orders" : "Track an order"}</b>
              </span>
            </Go>
            <button type="button" className="bar-cart" onClick={onCart} aria-label={`Open cart, ${basket.count} ${basket.count === 1 ? "item" : "items"}`}>
              <IconCart size={24} />
              {basket.count > 0 && <span className="count" aria-hidden="true">{basket.count}</span>}
            </button>
          </div>
        </div>
        <nav className="navrow" aria-label="Main">
          <div className="wrap navrow-in">
            <MegaMenu roots={roots} go={go} />
            <a className="navlink" href={`${MOUNT}/#specials`} onClick={(e) => { e.preventDefault(); go({ name: "home" }); window.setTimeout(() => document.getElementById("specials")?.scrollIntoView(), 60); }}>Specials</a>
            <a className="navlink" href={`${MOUNT}/#deal`} onClick={(e) => { e.preventDefault(); go({ name: "home" }); window.setTimeout(() => (document.getElementById("deal") ?? document.getElementById("specials"))?.scrollIntoView(), 60); }}>Deal of the day</a>
            <Go to={{ name: "clearance" }} go={go} className="navlink" ariaCurrent={route.name === "clearance" ? "page" : undefined}>Clearance sale</Go>
            {/* Bundles & Kits (shop-bundles.ts): the list, and its themes as sub-links on hover / focus. */}
            <div className="navdrop">
              <Go to={{ name: "bundles" }} go={go} className="navlink" ariaCurrent={route.name === "bundles" || route.name === "bundle" ? "page" : undefined}>Bundles &amp; Kits</Go>
              {bundleThemes.length > 0 && (
                <ul className="navdrop-menu" aria-label="Bundle themes">
                  {bundleThemes.map((t) => <li key={t.slug}><Go to={{ name: "bundles", theme: t.slug }} go={go}>{t.theme}</Go></li>)}
                </ul>
              )}
            </div>
            <Go to={{ name: "collections" }} go={go} className="navlink" ariaCurrent={route.name === "collections" ? "page" : undefined}>All collections</Go>
            <Go to={{ name: "guides" }} go={go} className="navlink" ariaCurrent={route.name === "guides" || route.name === "guide" ? "page" : undefined}>Buying guides</Go>
            <Go to={{ name: "faq" }} go={go} className="navlink" ariaCurrent={route.name === "faq" ? "page" : undefined}>Help</Go>
            <Go to={{ name: "contact" }} go={go} className="navlink" ariaCurrent={route.name === "contact" ? "page" : undefined}>Contact</Go>
          </div>
        </nav>
      </header>
      <MobileMenu open={menu} onClose={() => setMenu(false)} roots={roots} go={go} account={account} />
      {/* Outside the header: its scroll transform would otherwise trap the fixed-position panel. */}
      <PhotoSearch open={photo} onClose={() => setPhoto(false)} go={go} trade={trade} />
    </>
  );
}

/* ── Cart drawer ───────────────────────────────────────────────────────────────────────────── */
export function CartDrawer({ open, onClose, basket, go, freeDeliveryOverCents = null }: { open: boolean; onClose: () => void; basket: Basket; go: GoFn; freeDeliveryOverCents?: number | null }) {
  const freeLine = freeDeliveryLine(freeDeliveryOverCents, basket.subtotalCents);
  return (
    <Drawer
      open={open}
      onClose={onClose}
      side="right"
      title="Your cart"
      footer={basket.lines.length > 0 ? (
        <>
          <div className="cart-sub"><span>Subtotal</span><b className="money">{rands(basket.subtotalCents)}</b></div>
          {freeLine && <p className="fine" role="status" style={{ margin: ".25rem 0 .5rem" }}>{freeLine}</p>}
          <div className="cart-btns">
            <Go to={{ name: "basket" }} go={go} className="btn btn-quiet" onNavigate={onClose}>View cart</Go>
            <Go to={{ name: "checkout" }} go={go} className="btn btn-action" onNavigate={onClose}>Checkout</Go>
          </div>
        </>
      ) : undefined}
    >
      {basket.lines.length === 0 ? (
        <p className="cart-empty">Your cart is empty. Everything you add waits here, even if you close the tab.</p>
      ) : (
        <ul className="cart-lines">
          {basket.lines.map((l) => (
            <li key={l.variantId} className="cart-line">
              <div className="cart-img">{l.image && <Pic src={l.image} alt={l.variantTitle ? `${l.title} - ${l.variantTitle}` : l.title} sizes="64px" width={64} height={64} />}</div>
              <div>
                <Go to={lineRoute(l)} go={go} className="cart-title" onNavigate={onClose}>{l.title}</Go>
                {l.variantTitle && <div className="cart-var">{l.variantTitle}</div>}
                <div className="qty qty-sm">
                  <button type="button" onClick={() => basket.setQty(l.variantId, l.quantity - 1)} aria-label={`Fewer ${l.title}`}><IconMinus /></button>
                  <span aria-live="polite">{l.quantity}</span>
                  <button type="button" onClick={() => basket.setQty(l.variantId, Math.min(500, l.quantity + 1))} aria-label={`More ${l.title}`}><IconPlus /></button>
                </div>
              </div>
              <div className="cart-right">
                <b className="money">{rands((l.priceCents ?? 0) * l.quantity)}</b>
                <button type="button" className="remove" onClick={() => basket.setQty(l.variantId, 0)}>Remove</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Drawer>
  );
}
